import React, { useEffect, useState } from 'react';
import { AWAY_SHARE, EXPOSURE_YEAR } from '../../config/exposure';
import { getExposure } from '../../services/exposureApi';
import type { Exposure, ExposureMoment, ExposureTag } from '../../services/exposureApi';
import { exposureMockEnabled } from '../../services/draftMocks';

const TAG_HELP: Record<ExposureTag, string> = {
  register: 'register: counted in an official register (SSB), not estimated.',
  rule: 'rule: worked out from register counts by a fixed rule.',
  assumption: `assumption: depends on a guess that ${Math.round(AWAY_SHARE * 100)}% of residents are away at work or school on a weekday daytime.`,
};

const MOMENTS: Array<{ id: ExposureMoment; label: string; hours: string }> = [
  { id: 'night', label: 'Night', hours: '22–06' },
  { id: 'weekday_day', label: 'Weekday day', hours: '08–16' },
  { id: 'weekend_day', label: 'Weekend day', hours: '' },
  { id: 'evening', label: 'Evening', hours: '' },
];

export const TagChip: React.FC<{ tag: ExposureTag }> = ({ tag }) => (
  <span
    title={TAG_HELP[tag]}
    className="inline-flex cursor-help items-center rounded-full bg-paper-200 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-ink-muted"
  >
    {tag}
  </span>
);

const fmt = (n: number) => n.toLocaleString();

/**
 * "Population baseline": the typical population of an area, from registers (residents,
 * and employees at workplaces) and fixed rules. Always worded as typical or registered; never as a
 * count of who is there now.
 */
const PopulationBaseline: React.FC<{
  /** The area or areas, as WKT. Empty: nothing picked yet. */
  geometries: string[];
  /** Name of the area, project or "all monitored areas". */
  areaLabel?: string;
}> = ({ geometries, areaLabel }) => {
  const [exposure, setExposure] = useState<Exposure | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(geometries);

  useEffect(() => {
    const list: string[] = JSON.parse(key);
    setError(null);
    if (list.length === 0) {
      setExposure(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getExposure({ geometries: list, year: EXPOSURE_YEAR })
      .then((e) => {
        if (!cancelled) setExposure(e);
      })
      .catch((err) => {
        if (!cancelled) {
          setExposure(null);
          setError(err instanceof Error ? err.message : 'Could not load the population baseline.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return (
    <section
      aria-label="Population baseline"
      className="mb-6 overflow-hidden rounded-2xl border border-paper-300/70 bg-surface shadow-soft"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-paper-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-lg font-semibold text-ink">Population baseline</h2>
          {exposureMockEnabled() ? (
            <span className="badge badge-warning badge-outline badge-sm" title="Figures are made up for the demo until the exposure endpoint is live.">
              Draft data
            </span>
          ) : null}
        </div>
        {areaLabel ? <span className="text-sm text-ink-muted">{areaLabel}</span> : null}
      </div>

      <div className="space-y-5 px-4 py-4">
        {geometries.length === 0 ? (
          <p className="text-sm text-ink-muted">Pick an area to see its population baseline.</p>
        ) : error ? (
          <p role="alert" className="text-sm text-bad-ink">{error}</p>
        ) : loading || !exposure ? (
          <p className="text-sm text-ink-muted">Loading the population baseline…</p>
        ) : (
          <>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="flex items-center gap-2 text-sm text-ink-muted">
                  Registered residents <TagChip tag={exposure.tags.residents} />
                </dt>
                <dd className="font-display text-3xl font-bold text-ink">{fmt(exposure.residents)}</dd>
                <p className="text-xs text-ink-faint">People registered as living here</p>
              </div>
              <div>
                <dt className="flex items-center gap-2 text-sm text-ink-muted">
                  Employees at workplaces <TagChip tag={exposure.tags.workers} />
                </dt>
                <dd className="font-display text-3xl font-bold text-ink">{fmt(exposure.workers)}</dd>
                <p className="text-xs text-ink-faint">Employees counted at workplaces here</p>
              </div>
            </dl>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-ink">Typical total by time of day</h3>
              <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {MOMENTS.map((m) => (
                  <li key={m.id} className="rounded-xl border border-paper-200 px-3 py-2">
                    <p className="text-xs text-ink-muted">
                      {m.label}
                      {m.hours ? ` (${m.hours})` : ''}
                    </p>
                    <p className="font-display text-xl font-semibold text-ink">{fmt(exposure.typical[m.id])}</p>
                    <TagChip tag={exposure.tags[m.id]} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-1 text-xs text-ink-faint">
              <p>
                {`Source: ${exposure.sources[0].name}, ${exposure.sources[0].licence} · ${exposure.sources.map((s) => s.dataset).join(' · ')}`}
              </p>
              <p>Typical day, not the actual day. Area-weighted from 250 m cells.</p>
              <p>Cabin users are not included yet.</p>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PopulationBaseline;
